/**
 * Column chart. Token drawn, no chart library. Moved out of
 * screens/project/Observability.tsx so the admin Platform screen can draw its
 * daily request columns with the exact same marks rather than a lookalike.
 * The `tf__` class names are kept from that screen, whose own visits line
 * shares the rule and baseline hairlines.
 */
import '../../styles/components/bars.css';

/** The gap between columns, as a fraction of one bucket. */
const COL_GAP = 0.28;

export interface BarsProps {
  values: number[];
  max: number;
  /** Deploy positions, in bucket units. Drawn over the columns, never under. */
  rules: number[];
  /** Ink for a description, a state token only where the data IS a state. */
  tone: 'ink' | 'error';
  /** Draws the last column at the heavier ink weight. Weight, never colour. */
  markLast: boolean;
  label: string;
  tall?: boolean;
}

/**
 * The columns. Token drawn, no chart library, and no colour except where the
 * data is genuinely a state.
 *
 * The viewBox is one unit per bucket and one hundred units tall, stretched to
 * whatever width the panel has. Distortion is fine for a filled rect and fatal
 * for a hairline, so the deploy rules and the baseline carry
 * vector-effect="non-scaling-stroke" and stay exactly one device pixel at any
 * width. That is what makes this honest at 390px and at 1440px without a
 * second breakpoint and without a measured layout.
 */
export function Bars({ values, max, rules, tone, markLast, label, tall = false }: BarsProps) {
  const n = Math.max(1, values.length);

  return (
    <svg
      className="tf__bars"
      data-tone={tone}
      data-tall={tall ? 'yes' : 'no'}
      viewBox={`0 0 ${n} 100`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      focusable="false"
    >
      {values.map((value, index) => {
        if (value <= 0) return null;
        // A day with one visit on it still draws something. A column rounded
        // away is a day the chart claims did not happen.
        const height = max <= 0 ? 0 : Math.max(1.5, (value / max) * 100);
        return (
          <rect
            className="tf__col"
            data-now={markLast && index === values.length - 1 ? 'yes' : 'no'}
            key={index}
            x={index + COL_GAP / 2}
            y={100 - height}
            width={1 - COL_GAP}
            height={height}
          />
        );
      })}

      {/* Stolen from Railway, and the best value per pixel on this page for a
          novice: "it changed right here" next to "you shipped right here". */}
      {rules.map((x, index) => (
        <line
          className="tf__rule"
          key={`rule-${index}`}
          x1={x}
          y1="0"
          x2={x}
          y2="100"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      <line
        className="tf__base"
        x1="0"
        y1="100"
        x2={n}
        y2="100"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
